import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ReactCountryFlag from "react-country-flag";
import { Bank, CompanyCurrencyRate, CompanyCurrencyRateInput, Currency, EditBankInput } from "../../../models/general";
import { AddCompanyRate, EditCompanyBaseCurrency, EditCompanyRate, GetCompanyRates, GetCurrencies, RemoveCompanyRate } from "../../../api/General/currency";
import { AddBank, EditCompanyBank, GetBanks, EditBank, DeleteBank } from "../../../api/General/bank";
import { getCompanyRules } from "../../../api/General/Company";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from "../../../components/ui/Table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import AppIcon from "../../../components/AppIcon";
import { Spinner } from "../../../components/ui/Spinner";
import { useToast, ToastRenderer } from "../../../hooks/useToast";
import { PageLayout } from "../../../components/ui/PageLayout";
import { Banknote } from "lucide-react";

function CurrencyCombobox({
  options,
  value,
  onChange,
  label,
}: {
  options: Currency[];
  value: Currency | null;
  onChange: (v: Currency | null) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const s = search.toLowerCase();
    return options.filter(c =>
      c.currencyCode?.toLowerCase().includes(s) || c.name?.toLowerCase().includes(s)
    );
  }, [options, search]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        onClick={() => setOpen(!open)}
      >
        {value ? (
          <span className="flex items-center gap-2">
            <ReactCountryFlag
              countryCode={(value.country?.iso?.toLowerCase() || value.country?.iso3?.toLowerCase()) as any}
              svg
              style={{ width: '1.3em', height: '1.3em' }}
            />
            <span className="font-semibold">{value.currencyCode}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">{label}</span>
        )}
        <AppIcon name="ChevronDown" size={16} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-popover text-popover-foreground border border-border rounded-md shadow-md">
          <div className="p-2 border-b border-border">
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-60 overflow-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No options</div>
            ) : (
              filtered.map(c => (
                <button
                  key={c.id}
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground rounded-sm"
                  onClick={() => { onChange(c); setOpen(false); setSearch(""); }}
                >
                  <ReactCountryFlag
                    countryCode={(c.country?.iso?.toLowerCase() || c.country?.iso3?.toLowerCase()) as any}
                    svg
                    style={{ width: '1.3em', height: '1.3em' }}
                  />
                  <span className="font-semibold">{c.currencyCode}</span>
                  <span className="text-muted-foreground text-xs">{c.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CompanyCurrencyRates() {
  const { t } = useTranslation();
  const company = useSelector((s: any) => s.user.company);
  const { toast, showToast, dismissToast } = useToast();

  const [textFields, setTextFields] = useState<{ currency1: Currency | null; rate: string }[]>([]);
  const [textFieldsCompany, setTextFieldsCompany] = useState<{ name: string; routingNumber: string }[]>([]);
  const [allCurrencies, setAllCurrencies] = useState<Currency[]>([]);
  const [editCompanyBaseCurr, setEditCompanyBaseCurr] = useState(false);
  const [companyCurrencyRates, setCompanyCurrencyRates] = useState<CompanyCurrencyRate[]>([]);
  const [companyCurrencies, setCompanyCurrencies] = useState<Currency[]>([]);
  const [companyBaseCurrency, setCompanyBaseCurrency] = useState<Currency | null>(null);
  const [editableFields, setEditableFields] = useState(false);
  const [editableFieldsCompany, setEditableFieldsCompany] = useState(false);
  const [newRate, setNewRate] = useState(0);
  const [baseCurrency, setBaseCurrency] = useState<Currency | null>(null);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [companyBank, setCompanyBank] = useState<Bank | null>(null);
  const [editCompanyBankMode, setEditCompanyBankMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRoutingNumber, setNewRoutingNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDeleteBank, setOpenDeleteBank] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [deleteIdBank, setDeleteIdBank] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [currencies, banksRes, rates, rules] = await Promise.all([
        GetCurrencies(),
        GetBanks(),
        GetCompanyRates(),
        getCompanyRules(company?.id)
      ]);

      const compCurrencies: Currency[] = (rates.payload || []).map((c: any) => ({
        id: c.foreignCurrency.id,
        country: c.foreignCurrency.country,
        countryId: c.foreignCurrency.countryId,
        currencyCode: c.foreignCurrency.currencyCode,
        currencySymbol: c.foreignCurrency.currencySymbol,
        name: c.foreignCurrency.name,
        createdOn: new Date(),
        isArchived: false
      }));

      setAllCurrencies(currencies.payload);
      setBanks(banksRes.payload);
      setCompanyCurrencyRates(rates.payload);
      setCompanyCurrencies(compCurrencies);
      setCompanyBaseCurrency(rules.payload.currency);
      setCompanyBank(rules.payload.bank);
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
      showToast("Failed to load data", 'error');
    }
  };

  const handleAddTextField = () => {
    setTextFields(prev => [...prev, { currency1: null, rate: "" }]);
  };

  const handleRemoveTextField = (index: number) => {
    setTextFields(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddTextFieldCompany = () => {
    setTextFieldsCompany(prev => [...prev, { name: "", routingNumber: "" }]);
  };

  const handleRemoveTextFieldCompany = (index: number) => {
    setTextFieldsCompany(prev => prev.filter((_, i) => i !== index));
  };

  const addNewCompanyRate = async (currency: Currency | null, rate: string, index: number) => {
    if (!currency || !rate || isNaN(parseFloat(rate)) || parseFloat(rate) <= 0) {
      showToast("Invalid rate", 'error');
      return;
    }
    const exists = companyCurrencyRates.some(r => r.foreignCurrencyId === currency.id);
    if (exists) {
      showToast("Currency already exists", 'error');
      return;
    }
    try {
      const input: CompanyCurrencyRateInput = {
        baseCurrencyId: companyBaseCurrency?.id as number,
        companyId: company?.id,
        foreignCurrencyId: currency.id,
        rate: rate
      };
      await AddCompanyRate(input);
      showToast("Rate added", 'success');
      handleRemoveTextField(index);
      fetchData();
    } catch (error) {
      console.error("Error adding company rate:", error);
      showToast("Error adding rate", 'error');
    }
  };

  const addNewBank = async (name: string, routingNumber: string, index: number) => {
    if (!name || !routingNumber) {
      showToast("Bank name & routing required", 'error');
      return;
    }
    const input: Bank = {
      id: 0, name, routingNumber,
      companyId: company.id, createdOn: new Date(), isArchived: false, company,
    };
    await AddBank(input);
    handleRemoveTextFieldCompany(index);
    fetchData();
  };

  const deleteCompanyRate = async () => {
    const remove = await RemoveCompanyRate(deleteId);
    if (remove.payload === false) {
      showToast("Cannot delete: in use", 'error');
    } else {
      showToast("Currency deleted", 'success');
    }
    setOpenDelete(false);
    fetchData();
  };

  const deleteBankHandler = async () => {
    await DeleteBank(deleteIdBank);
    showToast("Bank deleted", 'success');
    setOpenDeleteBank(false);
    fetchData();
  };

  const editCurrencyRate = async (id: number, foreignCurrencyId: number) => {
    setEditableFields(false);
    const input: CompanyCurrencyRateInput = {
      baseCurrencyId: companyBaseCurrency?.id as number,
      companyId: company?.id,
      foreignCurrencyId,
      rate: newRate,
    };
    await EditCompanyRate(id, input);
    showToast("Rate updated", 'success');
    fetchData();
  };

  const editBankHandler = async (data: Bank) => {
    setEditableFieldsCompany(false);
    const input: EditBankInput = {
      name: newName || data.name,
      routingNumber: newRoutingNumber || data.routingNumber,
    };
    await EditBank(input, data.id);
    setNewName("");
    setNewRoutingNumber("");
    showToast("Bank updated", 'success');
    fetchData();
  };

  const updateBaseCurrencyHandler = async () => {
    await EditCompanyBaseCurrency(baseCurrency?.id as number);
    setEditCompanyBaseCurr(false);
    showToast("Base currency updated", 'success');
    fetchData();
  };

  const updateBankHandler = async () => {
    const id = companyBank?.id as number;
    await EditCompanyBank(id);
    setEditCompanyBankMode(false);
    showToast("Company bank updated", 'success');
    fetchData();
  };

  const existingCurrencyIds = new Set(companyCurrencyRates.map(r => r.foreignCurrencyId));
  const addableCurrencies = allCurrencies.filter(c => !existingCurrencyIds.has(c.id) && c.id !== companyBaseCurrency?.id);

  // Bank combobox options for selecting company bank
  const bankOptions = banks.map(b => ({ value: b.id, label: b.name }));

  return (
    <PageLayout
      title={t("Currency & Banks")}
      icon={Banknote}
      accentColor="slate"
    >
      <div className="flex flex-col gap-4 max-w-[1400px]">
      {/* Delete Currency Dialog */}
      <Dialog open={openDelete} onOpenChange={setOpenDelete}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t("Delete Currency")}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">{t("Are you sure you want to delete?")}</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDelete(false)}>{t("Cancel")}</Button>
            <Button variant="destructive" onClick={deleteCompanyRate}>{t("Delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Bank Dialog */}
      <Dialog open={openDeleteBank} onOpenChange={setOpenDeleteBank}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t("Delete Bank")}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">{t("Are you sure you want to delete this bank?")}</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDeleteBank(false)}>{t("Cancel")}</Button>
            <Button variant="destructive" onClick={deleteBankHandler}>{t("Delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center items-center min-h-[240px]">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {/* Exchange Rates Section */}
          <div className="flex flex-col gap-4 p-4 section rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between w-full">
              <h2 className="text-lg font-semibold text-primary">{t("CURRENCY EXCHANGE RATE")}</h2>
              <Button
                variant="ghost"
                size="icon"
                title={t("Add exchange rate") as string}
                aria-label={t("add currency rate") as string}
                onClick={handleAddTextField}
              >
                <AppIcon name="PlusCircle" size={20} />
              </Button>
            </div>

            {/* Base Currency */}
            <div className="flex flex-col gap-2 max-w-sm">
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-primary">{t("Company Base Currency")}</span>
                <AppIcon name="Info" size={16} className="text-muted-foreground" />
              </div>
              {editCompanyBaseCurr ? (
                <div className="flex items-center gap-2">
                  <div className="w-[220px]">
                    <CurrencyCombobox
                      options={companyCurrencyRates.length === 0 ? allCurrencies : companyCurrencies}
                      value={baseCurrency}
                      onChange={setBaseCurrency}
                      label={t("Select Currency")}
                    />
                  </div>
                  <Button variant="ghost" size="icon" onClick={updateBaseCurrencyHandler} disabled={!baseCurrency} title={t("Save")}>
                    <AppIcon name="Check" size={16} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 border border-border rounded px-3 py-1 min-h-[40px]">
                    {companyBaseCurrency && (
                      <ReactCountryFlag
                        countryCode={companyBaseCurrency?.country?.iso?.toLowerCase() as any}
                        svg
                        style={{ width: '1.5em', height: '1.5em' }}
                      />
                    )}
                    <span className="font-semibold">{companyBaseCurrency?.currencyCode}</span>
                  </div>
                  <Button variant="ghost" size="icon" title={t("Edit base currency")} onClick={() => { setEditCompanyBaseCurr(true); setBaseCurrency(companyBaseCurrency); }}>
                    <AppIcon name="Pencil" size={16} />
                  </Button>
                </div>
              )}
            </div>

            {/* Rates Table */}
            <div className="rounded-lg border border-border max-w-[720px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("Currency")}</TableHead>
                    <TableHead className="text-end">{t("Rate")}</TableHead>
                    <TableHead className="text-end w-[120px]">{t("Actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companyBaseCurrency && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <div className="flex items-center gap-2">
                          <ReactCountryFlag
                            countryCode={companyBaseCurrency?.country?.iso?.toLowerCase() as any}
                            svg
                            style={{ width: '1.5em', height: '1.5em' }}
                          />
                          <span className="font-semibold">{companyBaseCurrency?.currencyCode}</span>
                          <span className="text-sm text-muted-foreground">= 1</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {companyCurrencyRates.map(rate => (
                    <TableRow key={rate.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {rate.foreignCurrency?.country?.iso && (
                            <ReactCountryFlag
                              countryCode={rate.foreignCurrency.country.iso.toLowerCase() as any}
                              svg
                              style={{ width: '1.5em', height: '1.5em' }}
                            />
                          )}
                          <span className="font-semibold">{rate.foreignCurrency?.currencyCode}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-end">
                        {editableFields && deleteId === rate.id ? (
                          <input
                            type="number"
                            defaultValue={rate.rate}
                            className="w-[100px] h-8 rounded-md border border-input bg-background px-2 py-1 text-sm text-end focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            onChange={(e) => setNewRate(parseFloat(e.target.value))}
                          />
                        ) : (
                          <span className="font-semibold">{rate.rate}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-end">
                        {editableFields && deleteId === rate.id ? (
                          <Button variant="ghost" size="icon" title={t("Save")} onClick={() => editCurrencyRate(rate.id, rate.foreignCurrencyId)} disabled={!(newRate > 0)}>
                            <AppIcon name="Check" size={16} />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" title={t("Edit")} onClick={() => { setEditableFields(true); setDeleteId(rate.id); setNewRate(rate.rate); }}>
                            <AppIcon name="Pencil" size={16} />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" title={t("Delete")} onClick={() => { setDeleteId(rate.id); setOpenDelete(true); }}>
                          <AppIcon name="Trash2" size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {textFields.map((data, index) => (
                    <TableRow key={"new-" + index}>
                      <TableCell>
                        <div className="w-[180px]">
                          <CurrencyCombobox
                            options={addableCurrencies}
                            value={data.currency1}
                            onChange={(v) => {
                              const tf = [...textFields];
                              tf[index].currency1 = v;
                              setTextFields(tf);
                            }}
                            label={t("Currency")}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-end">
                        <input
                          type="number"
                          placeholder={t("Rate")}
                          className="w-[120px] h-8 rounded-md border border-input bg-background px-2 py-1 text-sm text-end focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                          value={data.rate}
                          onChange={e => {
                            const tf = [...textFields];
                            tf[index].rate = e.target.value;
                            setTextFields(tf);
                          }}
                        />
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="icon" title={t("Save")} disabled={!(data.currency1 && parseFloat(data.rate) > 0)} onClick={() => addNewCompanyRate(data.currency1, data.rate, index)}>
                          <AppIcon name="Check" size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Banks Section */}
          <div className="flex flex-col gap-4 p-4 section rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between w-full">
              <h2 className="text-lg font-semibold text-primary">{t("COMPANY BANKS")}</h2>
              <Button variant="ghost" size="icon" title={t("Add bank") as string} aria-label={t("add bank") as string} onClick={handleAddTextFieldCompany}>
                <AppIcon name="PlusCircle" size={20} />
              </Button>
            </div>

            <div className="flex flex-col gap-2 max-w-sm">
              <span className="text-sm font-semibold text-primary">{t("Company Bank")}</span>
              {editCompanyBankMode ? (
                <div className="flex items-center gap-2">
                  <CurrencyCombobox
                    options={banks.map(b => ({ ...b, currencyCode: b.name, country: null } as any))}
                    value={companyBank ? { ...companyBank, currencyCode: companyBank.name, country: null } as any : null}
                    onChange={(v: any) => setCompanyBank(v ? { ...v, name: v.currencyCode } as any : null)}
                    label={t("Select Bank")}
                  />
                  <Button variant="ghost" size="icon" onClick={updateBankHandler} disabled={!companyBank} title={t("Save")}>
                    <AppIcon name="Check" size={16} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex items-center border border-border rounded px-3 py-1 min-h-[40px] w-[260px]">
                    <span className="font-semibold">{companyBank?.name}</span>
                  </div>
                  <Button variant="ghost" size="icon" title={t("Edit bank")} onClick={() => setEditCompanyBankMode(true)}>
                    <AppIcon name="Pencil" size={16} />
                  </Button>
                </div>
              )}
            </div>

            <hr className="border-border" />

            <div className="rounded-lg border border-border max-w-[760px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("Bank")}</TableHead>
                    <TableHead>{t("Routing Number")}</TableHead>
                    <TableHead className="text-end w-[120px]">{t("Actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {banks.map(b => (
                    <TableRow key={b.id}>
                      <TableCell>
                        {editableFieldsCompany && deleteIdBank === b.id ? (
                          <input
                            type="text"
                            defaultValue={b.name}
                            className="h-8 rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            onChange={(e) => setNewName(e.target.value)}
                          />
                        ) : (
                          <span className="font-semibold">{b.name}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editableFieldsCompany && deleteIdBank === b.id ? (
                          <input
                            type="text"
                            defaultValue={b.routingNumber}
                            className="h-8 rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            onChange={(e) => setNewRoutingNumber(e.target.value)}
                          />
                        ) : (
                          <span className="font-medium">{b.routingNumber}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-end">
                        {editableFieldsCompany && deleteIdBank === b.id ? (
                          <Button variant="ghost" size="icon" title={t("Save")} onClick={() => editBankHandler(b)} disabled={!(newName || newRoutingNumber)}>
                            <AppIcon name="Check" size={16} />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" title={t("Edit")} onClick={() => { setEditableFieldsCompany(true); setDeleteIdBank(b.id); }}>
                            <AppIcon name="Pencil" size={16} />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" title={t("Delete")} onClick={() => { setDeleteIdBank(b.id); setOpenDeleteBank(true); }}>
                          <AppIcon name="Trash2" size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {textFieldsCompany.map((data, index) => (
                    <TableRow key={"new-bank-" + index}>
                      <TableCell>
                        <input
                          type="text"
                          placeholder={t("Bank")}
                          className="h-8 rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                          value={data.name}
                          onChange={e => {
                            const tf = [...textFieldsCompany];
                            tf[index].name = e.target.value;
                            setTextFieldsCompany(tf);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <input
                          type="text"
                          placeholder={t("Routing Number")}
                          className="h-8 rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                          value={data.routingNumber}
                          onChange={e => {
                            const tf = [...textFieldsCompany];
                            tf[index].routingNumber = e.target.value;
                            setTextFieldsCompany(tf);
                          }}
                        />
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="icon" title={t("Save")} disabled={!(data.name && data.routingNumber)} onClick={() => addNewBank(data.name, data.routingNumber, index)}>
                          <AppIcon name="Check" size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
}
